import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { CreatePersonCreatedPayload, CreatePersonRolePreset } from '@/components/CreatePersonModal/CreatePersonModal';
import type { CatalogManageTabId } from '@/components/CatalogManageModal/catalogSearchTypes';
import type { LegalEntity, Studio } from '@/api/client';

export type OpenCatalogManageOptions = {
  tab?: CatalogManageTabId;
  personId?: string | null;
  studioId?: string | null;
  legalEntityId?: string | null;
  rolePreset?: CreatePersonRolePreset | null;
  editionIdPreset?: string | null;
  /** Префилл имени / названия при создании (не поиск) */
  nameSeed?: string;
  onPersonCreated?: (payload: CreatePersonCreatedPayload) => void;
  onStudioCreated?: (studio: Studio) => void;
  onLegalEntityCreated?: (entity: LegalEntity) => void;
};

export type CatalogManageOpener = (opts?: OpenCatalogManageOptions) => void;

type CatalogManageContextValue = {
  open: CatalogManageOpener;
  canManageCatalog: boolean;
};

const CatalogManageOpenerContext = createContext<CatalogManageContextValue | null>(null);

export function CatalogManageOpenerProvider({
  children,
  openCatalogManage,
  canManageCatalog = false,
}: {
  children: ReactNode;
  openCatalogManage: CatalogManageOpener;
  /** `capabilities.persons.write` — создание/редактирование каталога. */
  canManageCatalog?: boolean;
}) {
  const value = useMemo(
    () => ({ open: openCatalogManage, canManageCatalog }),
    [openCatalogManage, canManageCatalog]
  );
  return (
    <CatalogManageOpenerContext.Provider value={value}>
      {children}
    </CatalogManageOpenerContext.Provider>
  );
}

/** Вне провайдера — no-op. */
export function useCatalogManageOpener(): CatalogManageOpener {
  return useContext(CatalogManageOpenerContext)?.open ?? (() => {});
}

/** Вне провайдера — false. */
export function useCanManageCatalog(): boolean {
  return useContext(CatalogManageOpenerContext)?.canManageCatalog ?? false;
}
